/*
 * Lemonade Training Oy copy rights all right reserved.
 * Box Type Definition
 */

import { Timestamp } from "firebase/firestore";
import { VoucherSettings } from "./voucher";
import { StatutoryCancelMode } from "./membershipRequest";

/* -------------- [ Box Role Types ] ------------------ */
export type BoxRole = "admin" | "coach" | "athlete";
export type BoxMemberStatus = "active" | "inactive" | "pending" | "suspended";

/* -------------- [ Box Address ] ------------------ */
export type BoxAddress = {
  street: string;
  city: string;
  country: string;
  postalCode: string;
};

/* -------------- [ Box Contact Info ] ------------------ */
export type BoxContactInfo = {
  email: string;
  phone: string;
  website?: string | null;
};

/* -------------- [ Box Features Settings ] ------------------ */
export type BoxFeatures = {
  bookingEnabled: boolean;
  programsEnabled: boolean;
  coachingEnabled: boolean;
};

/* -------------- [ Box Booking Settings ] ------------------ */
export type BoxBookingSettings = {
  maxBookingsPerAthlete: number; // Maximum number of active bookings per athlete (0 = unlimited)
  maxWaitlistPerAthlete: number; // Maximum number of active waitlist entries per athlete (0 = unlimited)
};

/* -------------- [ Box Settings ] ------------------ */
export type BoxSettings = {
  /** Changes only through updateBox (rules deny a direct client change); readers validate with isUsableTimeZone. */
  timezone: string;
  locale: string;
  features: BoxFeatures;
  bookingSettings: BoxBookingSettings;
  coachesCanViewInternalClasses?: boolean; // If true, coaches can view internal classes (default: false)
  /**
   * Whether a member's statutory (illness/pregnancy) cancellation takes effect
   * the moment they declare it, or routes to the gym first. Absent means
   * "effective_on_submit": a gym opts OUT of the strict regime deliberately,
   * and can never be opted out by omission.
   */
  statutoryCancelMode?: StatutoryCancelMode;
};

/* -------------- [ Box Rooms ] ------------------ */
export const BOX_ROOMS_MAX = 20;
export const BOX_ROOM_NAME_MAX_LENGTH = 40;
/** Server-minted; clients never mint room ids. */
export const BOX_ROOM_ID_REGEX = /^room_[0-9a-f]{14}$/;

export type BoxRoom = {
  id: string;
  name: string;
  /** Concrete Timestamp.now(): Firestore rejects serverTimestamp() inside arrays. */
  createdAt?: Timestamp | Date;
};

/* -------------- [ Box Document ] ------------------ */
export type Box = {
  id: string;
  name: string;
  description?: string;
  logoURL?: string | null;
  bannerURL?: string | null;
  coverArtURL?: string | null;
  address: BoxAddress;
  contactInfo: BoxContactInfo;
  settings: BoxSettings;
  stripeAccountID?: string | null;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;

  /**
   * The gym's owner (not Product.ownerUID). Written by createBox and the
   * owner backfill only; absent = no recorded owner, so admin grants are
   * unavailable until one is set.
   */
  ownerUID?: string | null;
  /** Bumped by every staff-grant mutation; absent = 0. Clients echo it as expectedGrantVersion. */
  grantVersion?: number;

  // ====================================================================================================
  // Voucher Payment Configuration (NEW)
  // ====================================================================================================

  /** Voucher payment settings and provider configuration */
  voucherSettings?: VoucherSettings | null;

  /** Whether voucher payment is enabled for this box */
  voucherPaymentEnabled?: boolean;

  /**
   * Display order. Absent/null/empty = no rooms. Server-owned (only saveBoxRooms
   * and removeBoxRoom write it); read it through normalizeBoxRooms().
   */
  rooms?: BoxRoom[] | null;
};

/* -------------- [ Box Member Permissions ] ------------------ */
export type BoxMemberPermissions = {
  canCreatePrograms: boolean;
  canBookSessions: boolean;
  canManageAthletes: boolean;
};

/* -------------- [ Box Member Metadata ] ------------------ */
export type BoxMemberMetadata = {
  invitedBy: string; // userUID or "system" for membership-based members
  lastActiveAt?: Timestamp | Date | null;
  membershipProductID?: string | null; // Track which membership product added this member
};

/* -------------- [ Box Member Document ] ------------------ */
export type BoxMember = {
  userUID: string;
  /** Scalar view of roles: always highestRole(roles). */
  role: BoxRole;
  /** The role set, dual-written with role. Absent on legacy docs — read through boxMemberRoles(). */
  roles?: BoxRole[];
  joinedAt: Timestamp | Date;
  status: BoxMemberStatus;
  permissions: BoxMemberPermissions;
  metadata: BoxMemberMetadata;
  updatedAt?: Timestamp | Date | null;
};

/** Legacy-doc-safe reader for the role set; an empty array also falls back to role. */
export const boxMemberRoles = (
  m: Pick<BoxMember, "role" | "roles">,
): BoxRole[] => (m.roles?.length ? m.roles : [m.role]);

/* -------------- [ Box Program Access Level ] ------------------ */
export type BoxProgramAccessLevel = "all" | "coaches" | "athletes";

/* -------------- [ Box Program Document ] ------------------ */
export type BoxProgram = {
  name: string;
  description?: string;
  createdBy: string; // userUID
  isActive: boolean;
  accessLevel: BoxProgramAccessLevel;
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
};

/* -------------- [ Box Invitation Status ] ------------------ */
export type BoxInvitationStatus = "pending" | "accepted" | "declined";

/* -------------- [ Box Invitation Document ] ------------------ */
export type BoxInvitation = {
  email: string;
  role: "coach" | "athlete";
  invitedBy: string; // userUID
  status: BoxInvitationStatus;
  expiresAt?: Timestamp | Date | null;
  createdAt: Timestamp | Date;
  updatedAt?: Timestamp | Date | null;

  // Permissions for coach invitations (NEW)
  // Only applicable when role === "coach"
  permissions?: BoxMemberPermissions;

  // Coach type for coach invitations (NEW)
  // Only applicable when role === "coach"
  coachType?: "box";
};

/* -------------- [ Class Type Status ] ------------------ */
export type ClassTypeStatus = "active" | "inactive";

/**
 * Class Type Definition
 *
 * Represents a reusable template for creating scheduled classes.
 * Previously known as ClassTemplate (deprecated in v2.0.0).
 *
 * @property {string} id - Unique identifier for the class type
 * @property {string} name - Display name (e.g., "CrossFit", "Yoga")
 * @property {string | null} description - Optional description (sanitized HTML)
 * @property {number} capacity - Maximum athletes per class (1-100)
 * @property {number} duration - Duration in minutes (5-300)
 * @property {string | null} color - Hex color code for calendar display (e.g., "#FF5733")
 * @property {string[] | null | undefined} allowedMembershipProductIDs - Array of membership product IDs that can book this class type. If null, undefined, or empty array, all members can book.
 * @property {string[] | null} workoutProgramUIDs - Optional: linked workout program IDs
 * @property {string | null} roomID - Optional: default room for new classes of this type
 * @property {boolean} isInternalByDefault - If true, classes created from this type are hidden from members by default
 * @property {boolean} isActive - Whether this class type is active
 * @property {Timestamp | Date} createdAt - Creation timestamp
 * @property {Timestamp | Date} updatedAt - Last update timestamp
 */
export type ClassType = {
  id: string;
  name: string;
  description?: string | null;
  capacity: number; // Max number of athletes per class
  duration: number; // Duration in minutes
  color?: string | null; // Hex color code for calendar display (e.g., "#FF5733")
  allowedMembershipProductIDs?: string[] | null; // Optional: Array of membership product IDs that can book this class type. If null/undefined, all members can book.
  workoutProgramUIDs?: string[] | null; // Optional: linked workout program IDs
  /**
   * Default room for NEW classes created without an explicit roomID. Changing it
   * never touches existing classes and is not part of the class-type cascade.
   */
  roomID?: string | null;
  isInternalByDefault: boolean; // If true, classes created from this type are hidden from members by default (visible only to admins/coaches)
  isActive: boolean;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
};

/**
 * @deprecated Use ClassType instead. Will be removed in v3.0.0.
 *
 * This type alias is provided for backward compatibility during the v2.0.0 transition.
 * Please update your imports to use ClassType instead:
 *
 * ```typescript
 * // Before (v1.x)
 * import { ClassTemplate } from '@lemonadetraining/types';
 *
 * // After (v2.x+)
 * import { ClassType } from '@lemonadetraining/types';
 * ```
 */
export type ClassTemplate = ClassType;

/** getClassTypes request. Omitted `includeInactive` = active types only. */
export type GetClassTypesRequest = {
  boxID: string;
  /** Box admins only: also return turned-off types. Ignored for everyone else. */
  includeInactive?: boolean;
};

/** Admins only. Only turns a type on; deleteClassType turns it off. */
export type ReactivateClassTypeRequest = {
  boxID: string;
  classTypeID: string;
};

export type ReactivateClassTypeResponse = { success: true };

/* -------------- [ Class Type Helper Functions ] ------------------ */

const CLASS_CARD_HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

/**
 * Whether a stored ClassType.color / Class.classTemplateColor is a six-digit
 * hex a client may render directly. Colours predate any write-side validation,
 * so every surface guards the read and decides its own failure case — the
 * dashboard substitutes a token, mobile renders no dot at all.
 */
export function isClassCardHexColor(raw: unknown): raw is string {
  return typeof raw === "string" && CLASS_CARD_HEX_COLOR.test(raw);
}

/**
 * Type guard to check if an object is a valid ClassType
 *
 * @param obj - The object to check
 * @returns True if obj is a ClassType, false otherwise
 *
 * @example
 * ```typescript
 * if (isClassType(data)) {
 *   console.log(data.name); // TypeScript knows data is ClassType
 * }
 * ```
 */
export function isClassType(obj: unknown): obj is ClassType {
  if (!obj || typeof obj !== "object") return false;

  const ct = obj as Partial<ClassType>;

  return (
    typeof ct.id === "string" &&
    typeof ct.name === "string" &&
    typeof ct.capacity === "number" &&
    typeof ct.duration === "number" &&
    typeof ct.isInternalByDefault === "boolean" &&
    typeof ct.isActive === "boolean" &&
    (ct.allowedMembershipProductIDs === undefined ||
      ct.allowedMembershipProductIDs === null ||
      Array.isArray(ct.allowedMembershipProductIDs)) &&
    (ct.roomID === undefined ||
      ct.roomID === null ||
      typeof ct.roomID === "string")
  );
}

/**
 * Check if a class type allows all members to book
 *
 * Returns true if allowedMembershipProductIDs is null, undefined, or an empty array.
 *
 * @param classType - The class type to check
 * @returns True if all members can book, false if restricted to specific memberships
 *
 * @example
 * ```typescript
 * const classType: ClassType = { allowedMembershipProductIDs: null, ... };
 * allowsAllMembers(classType); // true
 *
 * const restrictedClass: ClassType = { allowedMembershipProductIDs: ["mem_123"], ... };
 * allowsAllMembers(restrictedClass); // false
 * ```
 */
export function allowsAllMembers(classType: ClassType): boolean {
  const allowed = classType.allowedMembershipProductIDs;
  return !allowed || allowed.length === 0;
}

/**
 * Check if a user can book a specific class type based on their membership
 *
 * @param userMembershipID - The user's membership product ID
 * @param classType - The class type to check
 * @returns True if the user can book this class type, false otherwise
 *
 * @example
 * ```typescript
 * const classType: ClassType = { allowedMembershipProductIDs: ["mem_123", "mem_456"], ... };
 * canUserBookClassType("mem_123", classType); // true
 * canUserBookClassType("mem_789", classType); // false
 *
 * const openClass: ClassType = { allowedMembershipProductIDs: null, ... };
 * canUserBookClassType("any_membership", openClass); // true
 * ```
 */
export function canUserBookClassType(
  userMembershipID: string,
  classType: ClassType,
): boolean {
  const allowed = classType.allowedMembershipProductIDs;

  // If null, undefined, or empty array, all members can book
  if (!allowed || allowed.length === 0) return true;

  // Check if user's membership is in the allowed list
  return allowed.includes(userMembershipID);
}

/**
 * Get a human-readable label describing the access control for a class type
 *
 * @param classType - The class type to get the label for
 * @returns A descriptive label string
 *
 * @example
 * ```typescript
 * const openClass: ClassType = { allowedMembershipProductIDs: null, ... };
 * getAccessControlLabel(openClass); // "All Members"
 *
 * const restrictedClass: ClassType = { allowedMembershipProductIDs: ["mem_1", "mem_2"], ... };
 * getAccessControlLabel(restrictedClass); // "2 Specific Memberships"
 * ```
 */
export function getAccessControlLabel(classType: ClassType): string {
  const allowed = classType.allowedMembershipProductIDs;

  if (!allowed || allowed.length === 0) {
    return "All Members";
  }

  const count = allowed.length;
  return `${count} Specific Membership${count === 1 ? "" : "s"}`;
}

/* -------------- [ Box Room Helper Functions ] ------------------ */

/** Shape-only; id format and uniqueness are server-enforced. */
export function isBoxRoom(obj: unknown): obj is BoxRoom {
  if (!obj || typeof obj !== "object") return false;
  const r = obj as Partial<BoxRoom>;
  return typeof r.id === "string" && typeof r.name === "string";
}

/**
 * Degrades a malformed Box.rooms to what can be shown instead of crashing:
 * drops non-rooms and duplicate ids (first wins), caps at BOX_ROOMS_MAX.
 */
export function normalizeBoxRooms(rooms: unknown): BoxRoom[] {
  if (!Array.isArray(rooms)) return [];
  const seen = new Set<string>();
  const out: BoxRoom[] = [];
  for (const entry of rooms) {
    if (!isBoxRoom(entry) || seen.has(entry.id)) continue;
    seen.add(entry.id);
    out.push(entry);
    if (out.length === BOX_ROOMS_MAX) break;
  }
  return out;
}

/** null renders as "No room"; a dangling id is data drift, not an error. */
export function roomNameFor(
  rooms: readonly BoxRoom[],
  roomID: string | null | undefined,
): string | null {
  if (!roomID) return null;
  const room = rooms.find((r) => r.id === roomID);
  return room ? room.name : null;
}

/**
 * An explicit value wins even when null; only absent inherits. Not
 * `explicit ?? inherited`: that lets a default overwrite an explicit "no room".
 */
export function resolveRoomID(
  explicit: string | null | undefined,
  inherited: string | null | undefined,
): string | null {
  if (explicit !== undefined) return explicit;
  return inherited ?? null;
}

/* -------------- [ Class Status ] ------------------ */
export type ClassStatus = "scheduled" | "completed" | "cancelled";

/* -------------- [ Class Source Kind ] ------------------ */
export const CLASS_SOURCE_KINDS = ["template", "repeat"] as const;
export type ClassSourceKind = (typeof CLASS_SOURCE_KINDS)[number];

/* -------------- [ Class Document ] ------------------ */
export type Class = {
  id: string;
  classTemplateID: string;
  classTemplateName: string; // Denormalized for performance
  classTemplateColor?: string | null; // Denormalized class template color for calendar display
  allowedMembershipProductIDs: string[]; // Denormalized from class template for filtering - Array of membership product IDs that can access this class
  coachUID: string | null; // Assigned coach user ID; null = no coach
  coachName: string | null; // Denormalized coach name
  startTime: Timestamp | Date;
  endTime: Timestamp | Date; // Calculated: startTime + duration
  capacity: number; // Max capacity (copied from class template)
  currentBookings: number; // Real-time count of bookings
  status: ClassStatus;
  waitlistCount: number; // Real-time count of waitlist entries
  notes?: string | null; // Coach/admin notes
  isInternal: boolean; // If true, class is only visible to admins (and coaches if coachesCanViewInternalClasses is enabled)
  /**
   * null = "No room"; absent only on pre-rooms docs, read like null. Fixed at
   * creation (see resolveRoomID), never re-derived or cascaded from the class
   * type. An id no longer in Box.rooms reads as "No room".
   */
  roomID?: string | null;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
  /**
   * ID of the ScheduleTemplate this class was created from, if any.
   * Set by the template-apply flow; absent on hand-created classes. Optional +
   * nullable so Class docs that predate this field still satisfy the type — no
   * backfill or migration required.
   */
  sourceTemplateID?: string | null;
  /**
   * Opaque server-generated token shared by every Class created in a single
   * template-apply run. Undo of an apply deletes only still-unbooked classes whose
   * sourceTemplateID matches AND whose sourceApplyToken equals the token being
   * undone — without it, undoing apply #2 of a template could delete unbooked
   * classes from apply #1 of the SAME template (a cross-apply over-delete).
   * Never client-trusted. Optional + nullable for the same no-migration reason.
   */
  sourceApplyToken?: string | null;
  /**
   * UID of the staff user who ran the template apply that created this class.
   * Server-stamped (Cloud Functions only) and AUTHORIZATION-LOAD-BEARING:
   * undo scopes a non-admin coach to deleting only classes whose
   * sourceApplyUID is their own — a coach can never harvest another applier's
   * token and mass-delete their classes (that reach stays admin-only).
   * Never client-trusted. Optional + nullable so pre-existing Class docs and
   * hand-created classes satisfy the type — no backfill or migration required.
   */
  sourceApplyUID?: string | null;
  /**
   * Which server flow created this class: "template" (applyScheduleTemplate) or
   * "repeat" (duplicateWeek). Server-stamped. Undo deletes a class with no
   * sourceTemplateID only when this is "repeat". Absent on hand-created classes
   * and on classes created before the field existed.
   */
  sourceKind?: ClassSourceKind;
  /**
   * Set when status becomes "cancelled"; absent on every other status. A class
   * cancelled well before it was due carries no endTime-based signal that it
   * never ran, so this is the only record of when the gym called it off.
   */
  cancelledAt?: Timestamp | Date | null;
  /** Optional free text from the gym, stored on the class and its bookings. */
  cancellationReason?: string | null;
  /**
   * UID of the staff user who cancelled. Server-stamped and never client-written
   * (firestore.rules locks it): cancelling credits real entitlement balance and
   * emails the whole roster, and a gym can have several admins, so the record of
   * who did it has to be trustworthy.
   */
  cancelledByUID?: string | null;
  /**
   * Server-owned reminder dedupe: set when the 30-minute push went out; absent
   * = not yet sent. A real start-time move deletes it so the reminder fires
   * again at the new time. Never null: the reset is a delete.
   */
  reminderSentAt?: Timestamp | Date;
  /** Same, for the 24-hour email. */
  emailReminderSentAt?: Timestamp | Date;
};

/* -------------- [ Class Booking Status ] ------------------ */
export type ClassBookingStatus =
  "booked" | "checked_in" | "cancelled" | "no_show";

/* -------------- [ Class Booking Document ] ------------------ */
export type ClassBooking = {
  id: string;
  classID: string; // Reference to Classes document
  athleteUID: string; // Athlete user ID
  athleteName: string; // Denormalized athlete name
  membershipProductID?: string | null; // Membership used for booking (if applicable)
  status: ClassBookingStatus;
  isFromWaitlist: boolean; // True if promoted from waitlist
  waitlistPosition?: number | null; // Original waitlist position (if from waitlist)
  bookedAt: Timestamp | Date; // When booking was created
  checkedInAt?: Timestamp | Date | null; // When athlete checked in
  cancelledAt?: Timestamp | Date | null; // When booking was cancelled
  /**
   * Free text, or one of two server-written markers: "moved" when staff moved
   * the booking to another class (moveBooking), "account_deleted" when the
   * athlete's account was deleted before the class (userDeleted).
   */
  cancellationReason?: string | null;
  visitCardDeducted: boolean; // Track if visit card was deducted
  visitCardRefunded?: boolean | null; // Track if visit card was refunded on cancellation
  /** Staff uid when staff booked on the athlete's behalf; absent = self-booked. */
  bookedByUID?: string;
  /** Staff uid when staff cancelled on the athlete's behalf; absent = self-cancelled. */
  cancelledByUID?: string;
  /** On a booking cancelled with reason "moved": the booking that replaced it. */
  movedToBookingID?: string;
  /** On a booking created by moveBooking: the cancelled booking it replaced. */
  movedFromBookingID?: string;
  updatedAt?: Timestamp | Date;
};

/* -------------- [ Class Waitlist Document ] ------------------ */
export type ClassWaitlist = {
  id: string;
  classID: string; // Reference to Classes document
  athleteUID: string; // Athlete user ID
  athleteName: string; // Denormalized athlete name
  position: number; // Position in queue (1, 2, 3...) - FIFO
  joinedAt: Timestamp | Date; // When joined waitlist
  notifiedAt?: Timestamp | Date | null; // When notified of available spot
  expiresAt?: Timestamp | Date | null; // Expiration if not confirmed (15 min window)
};

/* -------------- [ Schedule Template Week Count ] ------------------ */

/** Allowed week counts for a schedule template — the single source of truth. */
export const SCHEDULE_TEMPLATE_WEEK_COUNTS = [1, 2, 4] as const;

/**
 * Number of distinct weeks a schedule template repeats over.
 * 1 = single-week pattern, 2 = A/B fortnight, 4 = a 4-week rotation
 * (NOT a calendar month). Derived from SCHEDULE_TEMPLATE_WEEK_COUNTS so the
 * runtime list and the compile-time union cannot drift.
 */
export type ScheduleTemplateWeekCount =
  (typeof SCHEDULE_TEMPLATE_WEEK_COUNTS)[number];

/* -------------- [ Schedule Template Slot ] ------------------ */
/**
 * One class occurrence inside a schedule template's repeating pattern.
 *
 * A slot is box-local and recurrence-free: it says "a class of this type, on this
 * day, at this wall-clock time, in this week of the pattern." The absolute UTC
 * startTime is resolved server-side at APPLY time against Box.settings.timezone
 * and is never stored on the slot.
 *
 * Denormalized classTypeName/classTypeColor are display-only and may go stale if
 * the class type is later renamed/recolored; apply re-reads the live ClassType and
 * denormalizes the CURRENT values onto the created Class.
 *
 * @property weekIndex - 0-based index into the pattern, 0..(weekCount-1).
 * @property dayOfWeek - 0 = Sunday .. 6 = Saturday.
 * @property startTime - Box-local wall-clock "HH:MM", 24h, minute-granular
 *   (e.g. "06:00", "18:30"). Server-validated against SCHEDULE_TEMPLATE_TIME_REGEX.
 * @property classTypeID - ID of a ClassType in this box (server-validated at save).
 * @property classTypeName - Denormalized ClassType.name for zero-read previews.
 * @property classTypeColor - Denormalized ClassType.color (hex) for grid previews.
 * @property coachUID - Optional pre-assigned coach UID; validated/overridden at apply.
 * @property coachName - Denormalized coach name when coachUID is set (display only).
 * @property isInternal - If true, classes created from this slot are members-hidden.
 * @property roomID - Tri-state room: absent = inherit at apply, null = no room.
 * @property notes - Optional free-text coach/admin note (server length-capped).
 */
export type ScheduleTemplateSlot = {
  weekIndex: number; // 0..(weekCount-1)
  dayOfWeek: number; // 0 = Sun .. 6 = Sat
  startTime: string; // box-local "HH:MM", 24h, minute-granular
  classTypeID: string;
  classTypeName: string; // denormalized for display/audit (may be stale)
  classTypeColor?: string | null; // denormalized for grid previews (zero-read)
  coachUID?: string | null; // optional; resolved/validated/overridden on apply
  coachName?: string | null; // denormalized when coachUID set
  isInternal?: boolean | null; // defaults to the class type's isInternalByDefault on apply
  /**
   * Absent = inherit the class type's CURRENT default at apply; null = explicitly
   * no room. An id no longer in Box.rooms resolves to null at apply.
   */
  roomID?: string | null;
  notes?: string | null;
};

export const SCHEDULE_TEMPLATE_INVALID_ROOMS =
  "schedule-template-invalid-rooms";

// Additive details on saveScheduleTemplate's invalid-argument error; no write occurs.
export type ScheduleTemplateInvalidRoomsDetails = {
  reason: typeof SCHEDULE_TEMPLATE_INVALID_ROOMS;
  // Zero-based positions in the submitted slots array, never persisted slot identities.
  slotIndices: number[];
};

/* -------------- [ Schedule Template Document ] ------------------ */
/**
 * A persisted, reusable "week-shape" for a box's class schedule.
 *
 * Lives at Boxes/{boxID}/ScheduleTemplates/{templateID} (box-scoped subcollection,
 * mirroring ClassTypes/Classes). Slots are EMBEDDED as an array on this doc (not a
 * subcollection): bounded to SCHEDULE_TEMPLATE_MAX_SLOTS, loads in one read, no
 * orphan-consistency problem. Applying a template only ever CREATES new Class docs;
 * it never updates or deletes existing ones.
 *
 * @property id - Firestore document ID.
 * @property name - Owner-chosen template name (server length-capped).
 * @property weekCount - 1 | 2 | 4 (see ScheduleTemplateWeekCount).
 * @property includesCoaches - Mirrors the Save dialog "Include coaches" toggle;
 *   when false, slots are applied coachless regardless of any stored coachUID.
 * @property slots - Embedded slot array; server-rejected beyond the max-slots cap.
 * @property isActive - Soft-active flag (inactive templates hidden from the picker).
 * @property createdBy - UID of the admin who authored the template.
 * @property lastAppliedAt - Timestamp of the most recent successful apply (MRU sort
 *   + "Last applied ..." label); null/absent until first apply.
 * @property createdAt - Creation timestamp.
 * @property updatedAt - Last-modified timestamp.
 */
export type ScheduleTemplate = {
  id: string;
  name: string;
  weekCount: ScheduleTemplateWeekCount;
  includesCoaches: boolean;
  slots: ScheduleTemplateSlot[];
  isActive: boolean;
  createdBy: string; // UID
  lastAppliedAt?: Timestamp | Date | null;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
};

/* -------------- [ Schedule Template Helper Functions ] ------------------ */

/** Hard cap on embedded slots per template (mirrors the server-side reject). */
export const SCHEDULE_TEMPLATE_MAX_SLOTS = 200;

/** Box-local wall-clock "HH:MM" (24h) — the canonical slot startTime format. */
export const SCHEDULE_TEMPLATE_TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * Type guard for a ScheduleTemplateSlot.
 *
 * Validates SHAPE only (field presence + primitive types). Cross-field / business
 * rules (weekIndex < weekCount, classTypeID belongs to the box, coachUID is a
 * member) are enforced server-side in the template callables — a type guard cannot
 * and must not assert authorization or cross-document facts.
 *
 * @param obj - The object to check
 * @returns True if obj is a ScheduleTemplateSlot, false otherwise
 */
export function isScheduleTemplateSlot(
  obj: unknown,
): obj is ScheduleTemplateSlot {
  if (!obj || typeof obj !== "object") return false;

  const s = obj as Partial<ScheduleTemplateSlot>;

  return (
    typeof s.weekIndex === "number" &&
    typeof s.dayOfWeek === "number" &&
    typeof s.startTime === "string" &&
    typeof s.classTypeID === "string" &&
    typeof s.classTypeName === "string" &&
    (s.classTypeColor === undefined ||
      s.classTypeColor === null ||
      typeof s.classTypeColor === "string") &&
    (s.coachUID === undefined ||
      s.coachUID === null ||
      typeof s.coachUID === "string") &&
    (s.coachName === undefined ||
      s.coachName === null ||
      typeof s.coachName === "string") &&
    (s.isInternal === undefined ||
      s.isInternal === null ||
      typeof s.isInternal === "boolean") &&
    (s.roomID === undefined ||
      s.roomID === null ||
      typeof s.roomID === "string") &&
    (s.notes === undefined || s.notes === null || typeof s.notes === "string")
  );
}

/**
 * Type guard for a ScheduleTemplate document.
 *
 * Shape-only narrowing (see isScheduleTemplateSlot). Does NOT enforce the max-slots
 * cap or the weekCount enum at runtime — those are server-side invalid-argument
 * rejects; a guard's job is structural narrowing, not validation. Timestamp fields
 * (lastAppliedAt/createdAt/updatedAt) are not runtime-checked: Timestamp | Date
 * unions can't be typeof-narrowed without importing firebase into the guard —
 * same convention as isClassType.
 *
 * @param obj - The object to check
 * @returns True if obj is a ScheduleTemplate, false otherwise
 */
export function isScheduleTemplate(obj: unknown): obj is ScheduleTemplate {
  if (!obj || typeof obj !== "object") return false;

  const t = obj as Partial<ScheduleTemplate>;

  return (
    typeof t.id === "string" &&
    typeof t.name === "string" &&
    typeof t.weekCount === "number" &&
    typeof t.includesCoaches === "boolean" &&
    typeof t.isActive === "boolean" &&
    typeof t.createdBy === "string" &&
    Array.isArray(t.slots) &&
    t.slots.every(isScheduleTemplateSlot)
  );
}

/**
 * Narrow a number to ScheduleTemplateWeekCount.
 *
 * Use at the callable boundary to convert a validated payload number into the
 * literal-union type; pair with a server-side invalid-argument reject on false.
 *
 * @param n - The value to check
 * @returns True if n is exactly 1, 2, or 4
 */
export function isScheduleTemplateWeekCount(
  n: unknown,
): n is ScheduleTemplateWeekCount {
  return (SCHEDULE_TEMPLATE_WEEK_COUNTS as readonly unknown[]).includes(n);
}

/* -------------- [ Analytics & Reporting Types ] ------------------ */

/* -------------- [ Analytics Period Type ] ------------------ */
export type AnalyticsPeriod = "daily" | "weekly" | "monthly";

/* -------------- [ Class Type Statistics ] ------------------ */
export type ClassTypeStatistics = {
  classTypeName: string;
  classesScheduled: number;
  totalBookings: number;
  checkedIn: number;
  cancelled: number;
  noShow: number;
  averageAttendance: number;
  capacityUtilization: number;
  visitCardsUsed: number;
};

/* -------------- [ Coach Statistics ] ------------------ */
export type CoachStatistics = {
  coachUID: string; // Coach UID for fetching name on-demand
  classesTaught: number;
  totalBookings: number;
  checkedIn: number;
  averageAttendance: number;
  noShowRate: number;
};

/* -------------- [ Hourly Statistics ] ------------------ */
export type HourlyStatistics = {
  classesScheduled: number;
  totalBookings: number;
  averageAttendance: number;
};

/* -------------- [ Day of Week Statistics ] ------------------ */
export type DayOfWeekStatistics = {
  classesScheduled: number;
  totalBookings: number;
  averageAttendance: number;
};

/* -------------- [ Class Analytics Document ] ------------------ */
export type ClassAnalytics = {
  id: string; // Format: "YYYY-MM-DD" (date string)
  date: Timestamp | Date; // Date this analytics represents
  period: AnalyticsPeriod; // Aggregation period

  // Class Statistics
  totalClasses: number; // Total classes scheduled
  completedClasses: number; // Classes that completed
  cancelledClasses: number; // Classes cancelled
  internalClasses: number; // Internal/admin-only classes

  // Booking Statistics
  totalBookings: number; // Total bookings created
  checkedInBookings: number; // Bookings checked in
  cancelledBookings: number; // Bookings cancelled
  noShowBookings: number; // Bookings marked as no-show

  // Capacity Statistics
  totalCapacity: number; // Sum of all class capacities
  totalBooked: number; // Sum of all bookings
  averageCapacityUtilization: number; // Average utilization percentage
  classesAtCapacity: number; // Classes that reached capacity
  classesWithWaitlist: number; // Classes with waitlist

  // Waitlist Statistics
  totalWaitlistEntries: number; // Total waitlist entries created
  waitlistPromotions: number; // Promoted from waitlist
  averageWaitlistSize: number; // Average waitlist size per class

  // Revenue Statistics (Visit Cards)
  visitCardsDeducted: number; // Visit cards deducted
  visitCardsRefunded: number; // Visit cards refunded
  netVisitCardUsage: number; // Net usage (deducted - refunded)
  visitCardUsers: number; // Unique members who used visit cards
  subscriptionUsers: number; // Unique members who used subscription memberships

  // Member Statistics
  uniqueBookers: number; // Unique athletes who booked
  uniqueAttendees: number; // Unique athletes who attended
  newBookers: number; // First-time bookers
  returningBookers: number; // Returning bookers

  // Class Type Breakdown
  classTypeStats: {
    [classTypeID: string]: ClassTypeStatistics;
  };

  // Coach Breakdown
  coachStats: {
    [coachUID: string]: CoachStatistics;
  };

  // Time-based Breakdown
  hourlyStats: {
    [hour: string]: HourlyStatistics; // "0-23" format
  };

  dayOfWeekStats: {
    [day: string]: DayOfWeekStatistics; // "0-6" (Sunday-Saturday)
  };

  // Metadata
  calculatedAt: Timestamp | Date; // When this aggregation was calculated
  lastUpdated: Timestamp | Date; // Last time this was updated
  version: number; // Schema version for migration
};

/* -------------- [ Class Snapshot Document ] ------------------ */
export type ClassSnapshot = {
  id: string; // Format: "YYYY-MM-DD"
  snapshotDate: Timestamp | Date; // Date of snapshot

  // Class counts by status
  scheduledClasses: number;
  completedClasses: number;
  cancelledClasses: number;

  // Booking counts by status
  activeBookings: number; // booked + checked_in
  cancelledBookings: number;
  noShowBookings: number;

  // Capacity metrics
  totalCapacity: number;
  totalBooked: number;
  utilizationPercentage: number;

  // Waitlist metrics
  totalWaitlistEntries: number;
  averageWaitlistSize: number;

  // Revenue metrics
  visitCardsActive: number; // Total visit cards in system
  visitCardsUsed: number; // Used this day

  createdAt: Timestamp | Date;
};

/* -------------- [ Member Engagement Level ] ------------------ */
export type MemberEngagementLevel = "high" | "medium" | "low";

/* -------------- [ Class Type Breakdown for Member ] ------------------ */
export type MemberClassTypeBreakdown = {
  bookings: number;
  checkIns: number;
};

/* -------------- [ Member Analytics Document ] ------------------ */
export type MemberAnalytics = {
  id: string; // athleteUID
  athleteUID: string;
  /** Denormalized athlete name, written by the analytics trigger since before this field was declared */
  athleteName?: string;

  // Booking Statistics
  totalBookings: number; // All time
  checkedInBookings: number;
  cancelledBookings: number;
  noShowBookings: number;

  // Current Period Statistics
  periodStart: Timestamp | Date;
  periodEnd: Timestamp | Date;
  bookingsInPeriod: number;
  checkInsInPeriod: number;
  cancellationsInPeriod: number;

  // Engagement Metrics
  averageBookingsPerWeek: number;
  lastBookingDate: Timestamp | Date;
  lastCheckInDate: Timestamp | Date;
  daysSinceLastBooking: number;
  daysSinceLastCheckIn: number;

  // Class Type Preferences
  favoriteClassTypes: string[]; // Array of classTypeIDs
  classTypeBreakdown: {
    [classTypeID: string]: MemberClassTypeBreakdown;
  };

  // Visit Card Usage
  visitCardsUsed: number;
  visitCardsRemaining: number;

  // Status
  isActive: boolean; // Has booked in last 30 days
  engagementLevel: MemberEngagementLevel; // Based on booking frequency

  /**
   * True once the member has ever booked in this box: totalBookings > 0 or
   * lastBookingDate is set. Deleting a booking decrements the count but never
   * clears the date, so this only ever goes false→true.
   *
   * Stored materialization of the never-booked predicate. Both writers of this
   * document must stamp it, because the summary computes engagement counts in
   * memory while the member list filters in Firestore — which cannot filter on
   * a derived value. Without a stored field the two disagree on screen.
   * Absent only on documents written before the backfill.
   */
  hasBooked?: boolean;

  /**
   * Classes attended before joining Lemonade, recorded by a box admin at
   * migration. Written only by the setMemberPriorClasses callable, never by
   * the booking trigger. Absence means 0. The athlete-facing total is
   * (live checked-in classes + priorClasses); the split is never shown to
   * the athlete.
   */
  priorClasses?: number;

  lastUpdated: Timestamp | Date;
};

/* -------------- [ Report Template Date Range Preset ] ------------------ */
export type ReportDateRangePreset =
  | "today"
  | "yesterday"
  | "thisWeek"
  | "lastWeek"
  | "thisMonth"
  | "lastMonth"
  | "custom";

/* -------------- [ Report Template Grouping ] ------------------ */
export type ReportGrouping =
  "day" | "week" | "month" | "classType" | "coach" | "none";

/* -------------- [ Report Template Document ] ------------------ */
export type ReportTemplate = {
  id: string;
  name: string;
  description?: string | null;

  // Report Configuration
  reportType: "attendance" | "revenue" | "capacity" | "member" | "custom";
  dateRange: {
    start: Timestamp | Date;
    end: Timestamp | Date;
    preset?: ReportDateRangePreset | null;
  };

  // Filters
  filters: {
    classTypeIDs?: string[] | null;
    coachUIDs?: string[] | null;
    membershipProductIDs?: string[] | null;
    statuses?: string[] | null;
  };

  // Metrics to Include
  metrics: string[]; // Array of metric IDs

  // Grouping
  groupBy?: ReportGrouping | null;

  // Created By
  createdBy: string; // userUID
  createdAt: Timestamp | Date;
  lastUsed: Timestamp | Date;
};

/* -------------- [ API Request/Response Types ] ------------------ */

export type JoinWaitlistResponse = {
  success: boolean;
  waitlistID: string;
  position: number;
  message: string;
};

/**
 * Upper bound on a member's recorded pre-Lemonade class count. The value is
 * the contract; the callable rejects above it rather than clamping.
 */
export const MEMBER_PRIOR_CLASSES_MAX = 10000;

/**
 * Character cap on the migration source note, applied after trimming. The
 * value is the contract; the callable and the admin form both enforce it.
 */
export const PRIOR_CLASSES_SOURCE_NOTE_MAX_LENGTH = 120;

/**
 * Request payload for setting a member's pre-Lemonade class count
 * Used by the setMemberPriorClasses Cloud Function (admin-only, box-scoped).
 * SET semantics: priorClasses replaces the stored value.
 */
export type SetMemberPriorClassesRequest = {
  /** Box the member belongs to */
  boxID: string;
  /** Must be a member of boxID — verified server-side before the write */
  memberUID: string;
  /** Integer in [0, MEMBER_PRIOR_CLASSES_MAX]. Out of range is rejected server-side, never clamped */
  priorClasses: number;
  /**
   * Operator free text about a named person (PII), capped at
   * PRIOR_CLASSES_SOURCE_NOTE_MAX_LENGTH after trimming; empty after trimming
   * is stored as null. Persisted to the Firestore audit record only — never
   * logged, never echoed in an HttpsError message or details payload, never
   * returned by any callable, never sent to analytics.
   *
   * Stored raw after trimming. Any consumer that renders or exports this value
   * must escape it for its own sink — HTML escape for markup, a formula-prefix
   * guard for CSV (a note beginning =, +, -, or @ executes on open in Excel and
   * Sheets), template escaping for PDF or email.
   */
  sourceNote?: string;
  /**
   * Value the client had read; recorded in the audit row for diagnosis, not
   * enforced — the write is last-write-wins. Compare against the response's
   * previousValue to detect a concurrent admin edit after the fact.
   */
  expectedCurrent?: number;
};

/**
 * Response from setting a member's pre-Lemonade class count
 */
export type SetMemberPriorClassesResponse = {
  /** The persisted value */
  priorClasses: number;
  /** Value stored before this write; 0 when the field or document was absent */
  previousValue: number;
  /**
   * Live checked-in class count, computed server-side by an aggregation over
   * ClassBookings — the same query the mobile badge runs. Never sourced from
   * the drift-prone checkedInBookings counter.
   */
  checkedInLive: number;
};

/* -------------- [ Rooms Callables ] ------------------ */

/** One saveBoxRooms entry. Absent id = new room (the server mints it); any other key is rejected. */
export type SaveBoxRoomInput = {
  id?: string;
  name: string;
};

/**
 * `rooms` is the FULL desired list in display order. Adds, renames and reorders
 * only: a stored room missing from the list is invalid-argument, never a delete.
 */
export type SaveBoxRoomsRequest = {
  boxID: string;
  rooms: SaveBoxRoomInput[];
};

export type SaveBoxRoomsResponse = {
  success: true;
  rooms: BoxRoom[];
};

export type RemoveBoxRoomRequest = {
  boxID: string;
  roomID: string;
  /** Required explicit boolean; any other type is invalid-argument. true = count only. */
  dryRun: boolean;
};

/** Past classes are never counted or written. */
export type RemoveBoxRoomCounts = {
  classTypes: number;
  upcomingClasses: number;
  templateSlots: number;
};

export type RemoveBoxRoomResponse = {
  success: true;
  mode: "dry_run" | "remove";
  /** false = the id was not on the box: zero counts, a successful no-op, never an error. */
  found: boolean;
  counts: RemoveBoxRoomCounts;
  rooms: BoxRoom[];
};

/**
 * Cancelling keeps the class document and marks it "cancelled"; only that path
 * refunds visit cards and notifies athletes. Deleting a class (deleteClass) is
 * the legacy hard delete kept for dashboard bundles that predate cancelClass.
 */
export type CancelClassRequest = {
  boxID: string;
  classID: string;
  /**
   * Free text shown to athletes in the cancellation email and push, at most
   * CANCEL_CLASS_REASON_MAX_LENGTH characters; absent or empty is stored as null.
   */
  reason?: string;
};

export const CANCEL_CLASS_REASON_MAX_LENGTH = 200;

/** Counts of what the cancellation cascade actually did, for the admin's toast. */
export type CancelClassResponse = {
  success: true;
  cancelledBookings: number;
  refundedVisitCards: number;
  /**
   * Bookings that had a visit deducted which could NOT be given back, because
   * the membership row is gone or was never recorded. The booking is cancelled
   * either way; this is the count nobody could otherwise detect afterwards,
   * since such a booking is indistinguishable from one that cost no visit.
   */
  refundsUnavailable: number;
  clearedWaitlist: number;
  notifiedCount: number;
};

/**
 * The fields a client may send to updateClass; the server allowlist rejects any
 * other key with invalid-argument. Omit a key to leave the field alone: the
 * callable SDK sends undefined as null.
 */
export type UpdateClassData = {
  /** Input key only; the server stores it as classTemplateID. */
  classTypeID?: string;
  /** null clears the coach. */
  coachUID?: string | null;
  /** ISO 8601 on the wire; the server rejects a Date or Timestamp. */
  startTime?: string;
  notes?: string | null;
  capacity?: number;
  isInternal?: boolean;
  /** null = an explicit "No room". */
  roomID?: string | null;
};

/** Highest capacity a single class may be given through updateClass. */
export const CLASS_CAPACITY_MAX = 200;

/**
 * Most waiting-list promotions one capacity raise runs; anyone beyond it stays
 * in line and moves up on the next opening.
 */
export const CLASS_CAPACITY_RAISE_PROMOTIONS_MAX = 30;

export type UpdateClassRequest = {
  boxID: string;
  classID: string;
  updateData: UpdateClassData;
};

/** What happened to the moved-class notice. The class is saved in every case. */
export const CLASS_RESCHEDULE_NOTICE_OUTCOMES = [
  "sent",
  "no_recipients",
  "capped_class",
  "capped_caller",
  "error",
] as const;
export type ClassRescheduleNoticeOutcome =
  (typeof CLASS_RESCHEDULE_NOTICE_OUTCOMES)[number];

/**
 * Moved-class notice budgets. The per-caller cap is checked first; the
 * dashboard copy quotes these numbers, so both sides read them from here.
 */
export const CLASS_RESCHEDULE_NOTICE_CAPS = {
  caller: {
    key: "classRescheduleNotice",
    limit: 150,
    windowMs: 60 * 60 * 1000,
  },
  class: { limit: 3, windowMs: 24 * 60 * 60 * 1000 },
} as const;

export type UpdateClassResponse = {
  success: true;
  /**
   * Present only when a notice was due: the start minute changed, the class is
   * scheduled and not yet started, and classRescheduleNotifications is on.
   * Absent is exactly the pre-F029 response.
   */
  rescheduleNotice?: ClassRescheduleNoticeOutcome;
  /**
   * Present only when the request sent `capacity`: how many people the save
   * moved up from the waiting list (0 when nobody moved).
   */
  promotedFromWaitlist?: number;
  /** The bookings those promotions created, in line order. Same presence rule. */
  promotedBookingIDs?: string[];
  /**
   * Present only when an error stopped the promotions before the new spots were
   * filled. The capacity is saved either way.
   */
  promotionStopped?: true;
};
